<!--
 * @Author: Antigravity
 * @Date: 2026-03-25
 * @Description: 投诉件分析-投诉件发起量走势 Cell
-->

<template>
  <div class="w-full flex flex-col">
    <div class="w-full flex items-end justify-between my-16">
      <div class="text-16 font-bold text-black-primary">投诉件发起量走势</div>
    </div>

    <div class="card p-16 flex flex-col">
      <div class="flex justify-between items-center mb-8">
        <span class="text-12 text-[#999]">单位（件）</span>
        <span class="text-12 text-[#999]">向右拖动可查看更早数据</span>
      </div>

      <div v-if="loading" class="state-text">加载中...</div>
      <div v-else-if="!list.length" class="state-text">暂无数据</div>
      <div v-else ref="chartEl" class="chart-main"></div>

      <div v-if="!filterParams.beginTime" class="text-center text-12 text-[#999] my-8">默认查询近30天</div>

      <div class="divider w-full bg-[#EBEDF0] h-1 my-16"></div>

      <div class="summary-area text-left" v-if="apiData.total !== undefined">
        <div class="text-12 text-[#999] mb-8">当前查询时段</div>
        <div class="text-14 text-[#666] mb-12">
          {{ formatDateTime(apiData.startDate) }} 至 {{ formatDateTime(apiData.endDate) }}
        </div>
        <div class="text-16 font-bold text-[#1E2226] mb-16">
          累计投诉量 <span class="text-20">{{ formatNumber(apiData.total) }}</span><span class="text-14 font-normal ml-2">件</span>
        </div>

        <div class="flex items-center gap-24">
          <div class="ratio-item flex items-center" v-if="apiData.annual">
            <span class="text-14 font-bold text-[#333]">同比</span>
            <span class="flex items-center ml-4" :class="getColorClass(apiData.annual.increaseRatio)">
              <van-icon :name="getIconName(apiData.annual.increaseRatio)" v-if="apiData.annual.increaseRatio !== 0" />
              <span class="font-bold text-14 ml-2">{{ apiData.annual.ratio || '--' }}</span>
            </span>
            <img src="@/assets/complaint/ic_info_tips.png" class="size-14 ml-4" @click.stop="handleShowRuleTip('annual')" />
          </div>

          <div class="ratio-item flex items-center" v-if="apiData.sequential">
            <span class="text-14 font-bold text-[#333]">环比</span>
            <span class="flex items-center ml-4" :class="getColorClass(apiData.sequential.increaseRatio)">
              <van-icon :name="getIconName(apiData.sequential.increaseRatio)" v-if="apiData.sequential.increaseRatio !== 0" />
              <span class="font-bold text-14 ml-2">{{ apiData.sequential.ratio || '--' }}</span>
            </span>
            <img src="@/assets/complaint/ic_info_tips.png" class="size-14 ml-4" @click.stop="handleShowRuleTip('sequential')" />
          </div>
        </div>
      </div>
    </div>

    <TipsDialog
      v-if="isRuleDetailVisible"
      :key="'rule-tip-' + tipType"
      v-model="isRuleDetailVisible"
      title="统计说明"
      confirmButtonText="我知道了"
    >
      <div class="p-20 text-14 text-[#666] leading-24 text-left">
        <template v-if="tipType === 'annual'">
          <div class="mb-20">同比%=(本期投诉量 - 去年同期投诉量) ÷ 去年同期投诉量 × 100%。</div>
          <div class="mb-4">例：</div>
          <div>当前查询时段为：<br />{{ formatDateTime(apiData.startDate) }} 至<br />{{ formatDateTime(apiData.endDate) }}</div>
          <div class="mt-12">同比查询时段为：<br />{{ formatDateTime(apiData.annual && apiData.annual.startDate) }} 至<br />{{ formatDateTime(apiData.annual && apiData.annual.endDate) }}</div>
        </template>
        <template v-else-if="tipType === 'sequential'">
          <div class="mb-20">环比%=(本期投诉量 - 上期投诉量) ÷ 上期投诉量 × 100%。</div>
          <div class="mb-4">例：</div>
          <div>当前查询时段为：<br />{{ formatDateTime(apiData.startDate) }} 至<br />{{ formatDateTime(apiData.endDate) }}</div>
          <div class="mt-12">环比查询时段为：<br />{{ formatDateTime(apiData.sequential && apiData.sequential.startDate) }} 至<br />{{ formatDateTime(apiData.sequential && apiData.sequential.endDate) }}</div>
        </template>
      </div>
    </TipsDialog>
  </div>
</template>

<script>
import echarts from "echarts";
import APIS from "@/service/modules/complaintApis/modules/visualization.js";
import visualFilterMixin from "../../mixins/visualFilterMixin";
import TipsDialog from "../../comps/TipsDialog.vue";

export default {
  name: "ComplaintTrendCell",
  mixins: [visualFilterMixin],
  components: {
    TipsDialog
  },
  data() {
    return {
      chart: null,
      loading: false,
      list: [],
      apiData: {},
      isRuleDetailVisible: false,
      tipType: "annual",
      _onResize: null
    };
  },
  watch: {
    filterParams: {
      deep: true,
      handler() {
        this.isRuleDetailVisible = false;
      }
    },
    list: {
      deep: true,
      handler() {
        this.$nextTick(() => this.renderChart());
      }
    }
  },
  mounted() {
    this.ensureChart();
  },
  beforeDestroy() {
    if (this._onResize) window.removeEventListener("resize", this._onResize);
    if (this.chart) this.chart.dispose();
  },
  methods: {
    ensureChart() {
      const el = this.$refs.chartEl;
      if (!el) return;
      if (this.chart && typeof this.chart.getDom === "function" && this.chart.getDom() !== el) {
        this.chart.dispose();
        this.chart = null;
      }
      if (!this.chart) this.chart = echarts.init(el);
      if (!this._onResize) {
        this._onResize = () => {
          if (this.chart) this.chart.resize();
        };
        window.addEventListener("resize", this._onResize, { passive: true });
      }
    },
    getYAxisMax(values) {
      const maxValue = Math.max.apply(null, values.concat([0]));
      if (maxValue <= 0) return 4;
      const rough = maxValue / 4;
      const step = rough <= 10 ? 5 : rough <= 50 ? 10 : rough <= 100 ? 20 : 50;
      return Math.ceil(maxValue / step) * step;
    },
    buildOption(dates, values, yMax) {
      const total = dates.length;
      const windowSize = 10;
      const startValue = total > windowSize ? total - windowSize : 0;
      const endValue = total > 0 ? total - 1 : 0;
      return {
        animation: false,
        grid: { left: 30, right: 20, top: 20, bottom: 30, containLabel: false },
        tooltip: {
          trigger: "axis",
          confine: true,
          backgroundColor: "rgba(54, 54, 54, 0.9)",
          padding: [8, 12],
          textStyle: { color: "#fff", fontSize: 12 },
          extraCssText: "z-index: 99;",
          formatter: params => {
            const dataIndex = params[0].dataIndex;
            const item = this.list[dataIndex] || {};
            return `${item.date || "-"}<br/>发起投诉量: ${this.formatNumber(item.total || 0)}`;
          }
        },
        dataZoom: [
          {
            type: "inside",
            xAxisIndex: 0,
            zoomLock: true,
            startValue,
            endValue
          }
        ],
        xAxis: {
          type: "category",
          data: dates,
          boundaryGap: false,
          axisTick: { show: false },
          axisLine: { lineStyle: { color: "#F0F0F0" } },
          axisLabel: { color: "#999", fontSize: 12, margin: 12 }
        },
        yAxis: {
          type: "value",
          min: 0,
          max: yMax,
          interval: yMax / 4,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: "#999", fontSize: 12 },
          splitLine: { lineStyle: { color: "#F0F0F0", type: "dashed" } }
        },
        series: [
          {
            type: "line",
            data: values,
            symbol: "emptyCircle",
            symbolSize: 6,
            itemStyle: { color: "#5AC28A" },
            lineStyle: { color: "#5AC28A", width: 2 }
          }
        ]
      };
    },
    renderChart() {
      if (!this.list.length) return;

      const dates = this.list.map(item => {
        const d = item.date || "";
        if (d && d.length >= 10) {
          const parts = d.split("-");
          if (parts.length >= 3) return `${parseInt(parts[1], 10)}/${parseInt(parts[2], 10)}`;
        }
        return d;
      });
      const values = this.list.map(item => Number(item.total || 0));
      const yMax = this.getYAxisMax(values);

      this.$nextTick(() => {
        this.ensureChart();
        if (this.chart) {
          this.chart.setOption(this.buildOption(dates, values, yMax), true);
          this.chart.resize();
        }
      });
    },
    formatNumber(n) {
      if (!n && n !== 0) return "0";
      return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    formatDateTime(dt) {
      if (!dt) return "";
      return dt.replace(/-/g, "/");
    },
    getColorClass(val) {
      if (val === 1) return "text-[#F95353]";
      if (val === -1) return "text-[#36B374]";
      return "text-[#999]";
    },
    getIconName(val) {
      if (val === 1) return "arrow-up";
      if (val === -1) return "arrow-down";
      return "";
    },
    handleShowRuleTip(type) {
      this.tipType = type;
      this.isRuleDetailVisible = true;
    },
    onLoad() {
      this.isRuleDetailVisible = false;
      const { areaId, beginTime, endTime, rule, channel } = this.filterParams;
      const params = {
        cityId: areaId,
        startDate: beginTime,
        endDate: endTime,
        rule,
        channel
      };

      this.loading = true;
      APIS.getComplaintTrend(params)
        .then(res => {
          if (res && res.ret === 0) {
            this.apiData = res.data || {};
            this.list = Array.isArray(this.apiData.list) ? this.apiData.list : [];
          } else {
            this.apiData = {};
            this.list = [];
          }
        })
        .catch(() => {
          this.apiData = {};
          this.list = [];
        })
        .finally(() => {
          this.loading = false;
          this.$nextTick(() => this.renderChart());
        });
    }
  }
};
</script>

<style lang="less" scoped>
.card {
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.04);
}

.chart-main {
  height: 220px;
  width: 100%;
}

.state-text {
  width: 100%;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
}
</style>
